import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { useParams } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'

import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Input, Select, Textarea, Button, ErrorMessage } from '@baseV2'
import { Spacings } from '@styles'
import { getAddressListFromBoth, getUserByID } from '@data'
import { Address, User } from '@data/model'
import { updateUser } from '@data/state/action/user'
import { toggleDrawer } from '@data/state/action/root'
import { strings } from '@constants'
import { showToast } from '@data/state/action'

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
`

const AddressWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	opacity: ${({ show }) => (show ? 1 : 0)};
	visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
	transition: all 0.2s ease-in-out;
`

const RequiredMark = styled.span`
	color: ${({ theme }) => theme.colors.status.error.text};
`

const FooterActions = styled.div`
	display: flex;
	gap: ${Spacings.SPACING_2B};
	justify-content: flex-end;
	width: 100%;
`

const initialAddressState = {
	city: '',
	district: '',
	state: '',
	street_address: '',
	country: '',
}

const initialSearchAddressState = {
	value: '',
	valid: false,
}

const CreateAddress = ({
	addToUserAddresses,
	addToNewEnquiry,
	addCallback,
	updateNewEnquiryCallback,
	updateCallback,
	addToNewAuction,
	isAdmin,
	addToOrg,
	addToOrgCallback,
}) => {
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const [userData, setUserData] = useState({})
	const dispatch = useDispatch()

	const completeAddress = useRef()
	const timeoutId = useRef()
	const [loading, setLoading] = useState(false)
	const [addressList, setAddressList] = useState([])
	const [searchAddress, setSearchAddress] = useState(initialSearchAddressState)

	const [address, setAddress] = useState(initialAddressState)
	const [apiError, setApiError] = useState('')
	const [streetTouched, setStreetTouched] = useState(false)
	const userId = useParams()?.userId

	const enableButton =
		Boolean(searchAddress.value && searchAddress.value.toString().length >= 4 && !apiError && !loading) &&
		address?.street_address &&
		address?.pincode

	const searchAddressChangeHandler = (key) => (value) => {
		if (apiError) {
			setApiError(false)
		}
		setSearchAddress((val) => ({ ...val, [key]: String(value) }))
		setAddressList([])
	}

	const streetAddressChangeHandler = (value) => {
		setAddress((val) => ({
			...val,
			street_address: value,
		}))
	}

	useEffect(() => {
		if (userId) {
			getUserByID(userId).then((response) => {
				setUserData(response)
			})
		} else {
			setUserData(user)
		}
	}, [userId, user])

	const addAddressHandler = () => {
		if (!enableButton) return

		completeAddress.current.complete_address = address.street_address.trim()

		if (addToUserAddresses) {
			userData.addAddress(completeAddress.current)
			const updatedUser = new User({ ...userData })
			dispatch(updateUser(updatedUser, isAdmin))
		}

		if (addToNewEnquiry && updateNewEnquiryCallback) {
			updateNewEnquiryCallback({ shipping_address: completeAddress.current })
		}

		if (addToNewAuction && updateCallback) {
			updateCallback(completeAddress.current, true)
		}

		if (addCallback && typeof addCallback === 'function') {
			addCallback(completeAddress.current)
		}

		if (addToOrg) {
			addToOrgCallback(completeAddress.current)
		}

		dispatch(toggleDrawer(false))
	}

	const getAddresses = () => {
		clearTimeout(timeoutId.current)
		setLoading(true)
		getAddressListFromBoth(searchAddress.value)
			.then((response) => {
				if (!response || response?.length <= 0) {
					dispatch(showToast(true, 'No address found!', { type: TOAST_TYPE.ERROR }))
				}
				setAddressList(response)
				if (response?.length > 0) {
					completeAddress.current = new Address(response?.[0])
					setAddress(response?.[0])
				}
			})
			.catch((error) => {
				dispatch(showToast(true, 'No address found!', { type: TOAST_TYPE.ERROR }))
				console.log(error)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	useEffect(() => {
		clearTimeout(timeoutId.current)
		if (searchAddress.value && searchAddress.value.toString().length >= 4 && !loading) {
			timeoutId.current = setTimeout(getAddresses, 2000)
		}

		return () => {
			setAddress(initialAddressState)
			clearTimeout(timeoutId.current)
		}
	}, [searchAddress])

	const addressSelectHandler = (val) => {
		completeAddress.current = new Address(val)
		setAddress(val)
	}

	const handlePinCodeChange = (val) => {
		setAddress((_address) => ({
			..._address,
			pincode: val,
		}))
		completeAddress.current.pincode = val
	}

	const handleAddressChange = (key) => (value) => {
		setAddress((_address) => ({
			..._address,
			[key]: value,
		}))
		completeAddress.current[key] = value
	}

	const streetError = streetTouched && !address.street_address ? 'Complete address is required' : ''

	return (
		<>
			<DrawerBodyWrapper>
				<Container>
					<Input
						label={strings('search', 'address')}
						placeholder={'Search by pincode / city / state / address'}
						id='search-address'
						disabled={loading}
						value={searchAddress.value ?? ''}
						onChange={(e) => searchAddressChangeHandler('value')(e.target.value)}
					/>
					<AddressWrapper show={address && addressList.length > 0}>
						<Select
							id='select-address'
							label={strings('select', 'address')}
							data={addressList}
							primaryKey={'id'}
							displayKey={'complete_address'}
							value={addressList.find((item) => item.id === address.id) ?? {}}
							onChange={addressSelectHandler}
						/>
					</AddressWrapper>
					{apiError && <ErrorMessage>{apiError}</ErrorMessage>}
					<AddressWrapper show={address.city && address.state}>
						<Input
							label={strings('pincode')}
							id='pincode'
							min={1000}
							max={99999999}
							type='number'
							disabled={loading}
							required={true}
							value={address.pincode ?? ''}
							onChange={(e) => handlePinCodeChange(e.target.value)}
						/>
						<Input
							label={strings('city')}
							id='city'
							type='text'
							value={address.city}
							onChange={(e) => handleAddressChange('city')(e.target.value)}
						/>
						<Input
							label={strings('district')}
							id='district'
							type='text'
							value={address.district}
							onChange={(e) => handleAddressChange('district')(e.target.value)}
						/>
						<Input
							label={strings('state')}
							id='state'
							type='text'
							value={address.state}
							onChange={(e) => handleAddressChange('state')(e.target.value)}
						/>
						<Input label={strings('country')} id='country' type='text' value={address.country} disabled />
						<Textarea
							label={
								<>
									{strings('complete', 'address')} <RequiredMark>*</RequiredMark>
								</>
							}
							id='street_address'
							rows={2}
							required={true}
							placeholder={'Enter address'}
							value={address.street_address}
							error={streetError}
							onChange={(e) => streetAddressChangeHandler(e.target.value)}
							onBlur={() => setStreetTouched(true)}
						/>
					</AddressWrapper>
				</Container>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<FooterActions>
					<Button variant='outline' color='danger' onClick={() => dispatch(toggleDrawer(false))}>
						{strings('cancel')}
					</Button>
					<Button variant='solid' color='primary' disabled={!enableButton} onClick={addAddressHandler}>
						{strings('add_address')}
					</Button>
				</FooterActions>
			</DrawerFooterWrapper>
		</>
	)
}

export default CreateAddress
